import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ComissaoEstruturaFiltroResponse, ComissaoTipoComissaoResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
import {
    FiltrosComissoes,
    OpcaoFiltroComissao,
    OPCOES_ATAS_COMISSOES,
    OPCOES_CONVOCACOES_COMISSOES,
    OPCOES_PERIODO_COMISSOES,
    OPCOES_STATUS_ATA_COMISSOES,
    OPCOES_STATUS_COMISSOES,
    OPCOES_TIPO_COMISSOES,
    criarFiltrosComissoesIniciais
} from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';

type BlocoFiltroColapsavel = 'codigo' | 'periodo' | 'status' | 'tipoComissao' | 'comissaoGrupo' | 'membroConvidado' | 'assistente' | 'statusAta' | 'local' | 'convocacoes' | 'atas';
type EstadoBlocosColapsados = Record<BlocoFiltroColapsavel, boolean>;
type TipoFiltroEstrutura = 'nome' | 'codigo';
type StatusFiltroEstrutura = 'todos' | 'ativas' | 'extintas';

interface EstruturaComissaoNo {
    id: number;
    idEoSuperior: number | null;
    descricao: string;
    ativo: number;
    filhos: EstruturaComissaoNo[];
}

interface TipoComissaoRaizNo {
    idTipoComissaoCt: number;
    descricao: string;
    filhos: EstruturaComissaoNo[];
}

@Component({
    selector: 'app-filtros-comissoes',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, DialogModule],
    templateUrl: './filtros-comissoes.html',
    styleUrls: ['./filtros-comissoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Barra de filtros do dashboard de acompanhamento (reuniões): edita uma cópia local e só
 *  aplica no botão "Filtrar"; abre um seletor em árvore de comissões/CT/GT por tipo. */
export class FiltrosComissoesComponent implements OnChanges {
    /** Filtros aplicados vindos do pai (copiados para `filtrosLocais`). */
    readonly filtros = input<FiltrosComissoes>(criarFiltrosComissoesIniciais());
    /** Opções de local de reunião. */
    readonly opcoesLocais = input<OpcaoFiltroComissao[]>([]);
    /** Opções de assistente. */
    readonly opcoesAssistentes = input<OpcaoFiltroComissao[]>([]);
    /** Opções de status da ata. */
    readonly opcoesStatusAta = input<OpcaoFiltroComissao[]>(OPCOES_STATUS_ATA_COMISSOES);
    /** Tipos de comissão (raízes da árvore de estrutura). */
    readonly tiposComissao = input<ComissaoTipoComissaoResponse[]>([]);
    /** Estruturas organizacionais (nós da árvore). */
    readonly estruturasComissao = input<ComissaoEstruturaFiltroResponse[]>([]);
    /** Carregando estruturas para o seletor em árvore. */
    readonly carregandoEstruturasComissao = input(false);
    /** Mensagem de erro ao carregar estruturas. */
    readonly erroEstruturasComissao = input('');

    /** Emite os filtros ao "Filtrar". */
    readonly aplicarFiltros = output<FiltrosComissoes>();
    /** Emite ao "Limpar". */
    readonly limparFiltros = output<void>();
    /** Pede o carregamento das estruturas (ao abrir o seletor). */
    readonly carregarEstruturasComissao = output<void>();

    filtrosLocais: FiltrosComissoes = criarFiltrosComissoesIniciais();

    readonly opcoesPeriodo = OPCOES_PERIODO_COMISSOES;
    readonly opcoesStatus = OPCOES_STATUS_COMISSOES;
    readonly opcoesTipoComissao = OPCOES_TIPO_COMISSOES;
    readonly opcoesConvocacoes = OPCOES_CONVOCACOES_COMISSOES;
    readonly opcoesAtas = OPCOES_ATAS_COMISSOES;
    exibirDialogComissaoGrupo = false;
    tipoFiltroEstrutura: TipoFiltroEstrutura = 'nome';
    valorFiltroEstrutura = '';
    statusFiltroEstrutura: StatusFiltroEstrutura = 'ativas';
    nosRaizTiposComissao: TipoComissaoRaizNo[] = [];
    idsTiposExpandidos = new Set<number>();
    idsNosExpandidos = new Set<number>();
    noEstruturaSelecionado: EstruturaComissaoNo | null = null;
    blocosColapsados: EstadoBlocosColapsados = {
        codigo: true,
        periodo: true,
        status: true,
        tipoComissao: true,
        comissaoGrupo: true,
        membroConvidado: true,
        assistente: true,
        statusAta: true,
        local: true,
        convocacoes: true,
        atas: true
    };
    private estadoAntesDoColapso: EstadoBlocosColapsados | null = {
        codigo: false,
        periodo: false,
        status: false,
        tipoComissao: false,
        comissaoGrupo: false,
        membroConvidado: false,
        assistente: false,
        statusAta: false,
        local: false,
        convocacoes: false,
        atas: false
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filtros']) {
            this.filtrosLocais = { ...this.filtros() };
        }

        if (changes['tiposComissao'] || changes['estruturasComissao']) {
            this.nosRaizTiposComissao = this.montarArvoreEstruturaPorTipo(this.tiposComissao(), this.estruturasComissao());
            this.idsTiposExpandidos = new Set(this.coletarIdsTiposExpandidos(this.nosRaizTiposComissao));
            this.idsNosExpandidos = new Set(this.coletarIdsExpandidos(this.nosRaizTiposComissao.flatMap((tipo) => tipo.filhos)));
            this.noEstruturaSelecionado = this.buscarNoPorId(this.filtrosLocais.comissaoEstruturaId, this.nosRaizTiposComissao);
        }
    }

    aplicar(): void {
        this.aplicarFiltros.emit({ ...this.filtrosLocais });
    }

    limpar(): void {
        this.filtrosLocais = criarFiltrosComissoesIniciais();
        this.limparFiltros.emit();
        this.aplicarFiltros.emit({ ...this.filtrosLocais });
    }

    colapsarFiltros(): void {
        if (!this.estadoAntesDoColapso) {
            this.estadoAntesDoColapso = { ...this.blocosColapsados };
            this.blocosColapsados = {
                codigo: true,
                periodo: true,
                status: true,
                tipoComissao: true,
                comissaoGrupo: true,
                membroConvidado: true,
                assistente: true,
                statusAta: true,
                local: true,
                convocacoes: true,
                atas: true
            };
            return;
        }

        this.blocosColapsados = { ...this.estadoAntesDoColapso };
        this.estadoAntesDoColapso = null;
    }

    labelBotaoColapso(): string {
        return this.estadoAntesDoColapso ? 'Restaurar Filtros' : 'Colapsar Filtros';
    }

    buscarMembroConvidado(): void {
        this.aplicarFiltros.emit({ ...this.filtrosLocais });
    }

    limparMembroConvidado(): void {
        this.filtrosLocais.membroConvidado = '';
    }

    buscarComissaoGrupo(): void {
        this.exibirDialogComissaoGrupo = true;
        this.noEstruturaSelecionado = this.buscarNoPorId(this.filtrosLocais.comissaoEstruturaId, this.nosRaizTiposComissao);
        this.tipoFiltroEstrutura = 'nome';
        this.valorFiltroEstrutura = '';
        this.statusFiltroEstrutura = 'ativas';
        this.carregarEstruturasComissao.emit();
    }

    limparComissaoGrupo(): void {
        this.filtrosLocais.comissaoNome = '';
        this.filtrosLocais.comissaoEstruturaId = null;
    }

    alternarColapso(chave: BlocoFiltroColapsavel): void {
        this.blocosColapsados[chave] = !this.blocosColapsados[chave];
    }

    iconeColapso(chave: BlocoFiltroColapsavel): string {
        return this.blocosColapsados[chave] ? 'pi pi-chevron-down' : 'pi pi-chevron-up';
    }

    confirmarSelecaoComissaoGrupo(): void {
        if (!this.noEstruturaSelecionado) {
            return;
        }

        this.filtrosLocais.comissaoEstruturaId = this.noEstruturaSelecionado.id;
        this.filtrosLocais.comissaoNome = `${this.noEstruturaSelecionado.id} - ${this.noEstruturaSelecionado.descricao}`;
        this.exibirDialogComissaoGrupo = false;
    }

    cancelarSelecaoComissaoGrupo(): void {
        this.exibirDialogComissaoGrupo = false;
    }

    selecionarNoEstrutura(no: EstruturaComissaoNo): void {
        this.noEstruturaSelecionado = no;
    }

    alternarExpandidoEstrutura(id: number): void {
        if (this.idsNosExpandidos.has(id)) {
            this.idsNosExpandidos.delete(id);
            return;
        }
        this.idsNosExpandidos.add(id);
    }

    noEstruturaExpandido(id: number): boolean {
        return this.idsNosExpandidos.has(id);
    }

    noEstruturaSelecionadoAtivo(id: number): boolean {
        return this.noEstruturaSelecionado?.id === id;
    }

    alternarExpandidoTipo(idTipoComissaoCt: number): void {
        if (this.idsTiposExpandidos.has(idTipoComissaoCt)) {
            this.idsTiposExpandidos.delete(idTipoComissaoCt);
            return;
        }
        this.idsTiposExpandidos.add(idTipoComissaoCt);
    }

    tipoExpandido(idTipoComissaoCt: number): boolean {
        return this.idsTiposExpandidos.has(idTipoComissaoCt);
    }

    descricaoEstruturaSelecionada(): string {
        if (!this.filtrosLocais.comissaoEstruturaId || !this.filtrosLocais.comissaoNome) {
            return '';
        }
        return this.filtrosLocais.comissaoNome;
    }

    onValorFiltroEstruturaChange(valor: string): void {
        if (this.tipoFiltroEstrutura === 'codigo') {
            this.valorFiltroEstrutura = valor.replace(/\D/g, '');
            return;
        }

        this.valorFiltroEstrutura = valor.toUpperCase();
    }

    nosTipoComissaoExibicao(): TipoComissaoRaizNo[] {
        const termo = this.valorFiltroEstrutura.trim().toUpperCase();

        return this.nosRaizTiposComissao.map((tipo) => ({
            ...tipo,
            filhos: this.filtrarNosEstrutura(tipo.filhos, termo, this.tipoFiltroEstrutura, this.statusFiltroEstrutura)
        }));
    }

    trackTipoComissao(index: number, tipo: TipoComissaoRaizNo): string {
        return `${tipo.idTipoComissaoCt}-${index}`;
    }

    trackNoEstrutura(index: number, no: EstruturaComissaoNo): string {
        return `${no.id}-${index}`;
    }

    private montarArvoreEstruturaPorTipo(tipos: ComissaoTipoComissaoResponse[], estruturas: ComissaoEstruturaFiltroResponse[]): TipoComissaoRaizNo[] {
        const tiposRaiz = tipos
            .map((tipo) => ({
                idTipoComissaoCt: this.normalizarIdPositivo(tipo.idTipoComissaoCt),
                descricao: (tipo.dsTipoComissaoCt ?? '').trim()
            }))
            .filter((tipo): tipo is { idTipoComissaoCt: number; descricao: string } => tipo.idTipoComissaoCt !== null);

        const raizes: TipoComissaoRaizNo[] = tiposRaiz.map((tipo) => ({
            idTipoComissaoCt: tipo.idTipoComissaoCt,
            descricao: tipo.descricao || `Tipo ${tipo.idTipoComissaoCt}`,
            filhos: []
        }));

        for (const tipoRaiz of raizes) {
            const nosPorId = new Map<number, EstruturaComissaoNo>();
            const nosDoTipo: EstruturaComissaoNo[] = [];

            for (const item of estruturas) {
                const idTipoComissaoCt = this.normalizarIdPositivo(item.idTipoComissaoCt);
                if (idTipoComissaoCt !== tipoRaiz.idTipoComissaoCt) {
                    continue;
                }

                const id = this.normalizarIdPositivo(item.idEstruturaOrganizacional);
                if (id === null || nosPorId.has(id)) {
                    continue;
                }

                const no: EstruturaComissaoNo = {
                    id,
                    idEoSuperior: this.normalizarIdPositivo(item.idEoSuperior),
                    descricao: (item.dsEstruturaOrganizacional ?? '').trim(),
                    ativo: item.ativo ?? 0,
                    filhos: []
                };

                nosPorId.set(no.id, no);
                nosDoTipo.push(no);
            }

            for (const no of nosDoTipo) {
                const pai = no.idEoSuperior !== null ? nosPorId.get(no.idEoSuperior) : undefined;
                if (pai) {
                    pai.filhos.push(no);
                } else {
                    tipoRaiz.filhos.push(no);
                }
            }
        }

        return raizes;
    }

    private coletarIdsTiposExpandidos(tipos: TipoComissaoRaizNo[]): number[] {
        return tipos.filter((tipo) => tipo.filhos.length > 0).map((tipo) => tipo.idTipoComissaoCt);
    }

    private coletarIdsExpandidos(nos: EstruturaComissaoNo[]): number[] {
        const ids: number[] = [];

        for (const no of nos) {
            if (no.filhos.length > 0) {
                ids.push(no.id);
                ids.push(...this.coletarIdsExpandidos(no.filhos));
            }
        }

        return ids;
    }

    private buscarNoPorId(id: number | null, tipos: TipoComissaoRaizNo[]): EstruturaComissaoNo | null {
        if (!id) {
            return null;
        }

        for (const tipo of tipos) {
            const encontrado = this.buscarNoPorIdEmLista(id, tipo.filhos);
            if (encontrado) {
                return encontrado;
            }
        }

        return null;
    }

    private buscarNoPorIdEmLista(id: number, nos: EstruturaComissaoNo[]): EstruturaComissaoNo | null {
        if (!id) {
            return null;
        }

        for (const no of nos) {
            if (no.id === id) {
                return no;
            }

            const filho = this.buscarNoPorIdEmLista(id, no.filhos);
            if (filho) {
                return filho;
            }
        }

        return null;
    }

    private normalizarIdPositivo(valor?: number | null): number | null {
        if (typeof valor !== 'number' || !Number.isFinite(valor) || valor <= 0) {
            return null;
        }

        return Math.trunc(valor);
    }

    private filtrarNosEstrutura(nos: EstruturaComissaoNo[], termo: string, tipoFiltro: TipoFiltroEstrutura, statusFiltro: StatusFiltroEstrutura): EstruturaComissaoNo[] {
        const filtrados: EstruturaComissaoNo[] = [];

        for (const no of nos) {
            const filhosFiltrados = this.filtrarNosEstrutura(no.filhos, termo, tipoFiltro, statusFiltro);
            const corresponde = this.noCorrespondeFiltro(no, termo, tipoFiltro, statusFiltro);

            if (corresponde) {
                filtrados.push({ ...no, filhos: filhosFiltrados });
            } else if (filhosFiltrados.length > 0) {
                // Replica o legado: se o pai nao entrou no filtro, os filhos validos sobem um nivel.
                filtrados.push(...filhosFiltrados);
            }
        }

        return filtrados;
    }

    private noCorrespondeFiltro(no: EstruturaComissaoNo, termo: string, tipoFiltro: TipoFiltroEstrutura, statusFiltro: StatusFiltroEstrutura): boolean {
        const statusValido = this.statusNoValido(no, statusFiltro);
        if (!statusValido) {
            return false;
        }

        if (!termo) {
            return true;
        }

        if (tipoFiltro === 'codigo') {
            return String(no.id).includes(termo);
        }

        return no.descricao.toUpperCase().includes(termo);
    }

    private statusNoValido(no: EstruturaComissaoNo, statusFiltro: StatusFiltroEstrutura): boolean {
        if (statusFiltro === 'todos') {
            return true;
        }

        if (statusFiltro === 'ativas') {
            return no.ativo === 1;
        }

        return no.ativo !== 1;
    }
}
